/* ==========================================================================
   Calda - global behaviour
   Drawers, mobile menu, predictive search, the Ajax cart, quick add,
   the hero slider, marquee-free carousels and the before/after comparison.

   Everything that touches the cart goes through Shopify's Ajax Cart API and
   the Section Rendering API. Checkout is never intercepted, so Shopify's
   own analytics and any pixel apps keep working untouched.
   ========================================================================== */

(function () {
  'use strict';

  var config = window.Evora || {};
  var routes = config.routes || {};
  var strings = config.strings || {};
  var settings = config.settings || {};
  var isRTL = document.documentElement.getAttribute('dir') === 'rtl';

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $$(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /* ------------------------------------------------------------ Money ---- */

  function formatMoney(cents, format) {
    var moneyFormat = format || config.moneyFormat || '{{amount}}';
    var value = typeof cents === 'string' ? cents.replace('.', '') : cents;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'number' ? precision : 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) return '0';

      number = (number / 100).toFixed(precision);

      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimal + parts[1] : '';

      return whole + fraction;
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = moneyFormat.match(placeholder);
    var formatted = '';

    switch (match && match[1]) {
      case 'amount':
        formatted = formatWithDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(value, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        formatted = formatWithDelimiters(value, 2, ' ', '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2);
    }

    if (!match) return formatted;

    return moneyFormat.replace(placeholder, formatted);
  }

  /* ---------------------------------------------------------- Drawers ---- */

  var Drawers = {
    lastFocused: null,

    get overlay() {
      return $('[data-overlay]');
    },

    open: function (name) {
      var drawer = $('[data-drawer="' + name + '"]');
      if (!drawer) return;

      this.closeAll(true);
      this.lastFocused = document.activeElement;

      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      // Force a reflow so the transition runs from the off-screen position.
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.body.classList.add('drawer-open');

      if (this.overlay) {
        this.overlay.hidden = false;
        void this.overlay.offsetWidth;
        this.overlay.classList.add('is-visible');
      }

      $$('[aria-controls]').forEach(function (trigger) {
        if (trigger.getAttribute('aria-controls') === drawer.id) {
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      var focusTarget = drawer.querySelector('[autofocus], input:not([type="hidden"]), button, a[href]');
      if (focusTarget) {
        setTimeout(function () {
          focusTarget.focus();
        }, 60);
      }
    },

    close: function (drawer, skipOverlay) {
      if (!drawer) return;

      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');

      $$('[aria-controls]').forEach(function (trigger) {
        if (trigger.getAttribute('aria-controls') === drawer.id) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      });

      setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 320);

      if (!skipOverlay) this.hideChrome();
    },

    closeAll: function (skipOverlay) {
      var self = this;
      $$('[data-drawer]').forEach(function (drawer) {
        if (drawer.classList.contains('is-open')) self.close(drawer, true);
      });
      if (!skipOverlay) this.hideChrome();
    },

    hideChrome: function () {
      document.body.classList.remove('drawer-open');

      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
        var overlay = this.overlay;
        setTimeout(function () {
          if (!overlay.classList.contains('is-visible')) overlay.hidden = true;
        }, 300);
      }

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
        this.lastFocused = null;
      }
    }
  };

  // The mobile menu markup lives inside the header section so it can read the
  // merchant's menu setting, but a sticky header with a z-index creates its own
  // stacking context, which would trap the drawer underneath the page overlay.
  // Moving it to <body> puts it back in the root stacking context.
  function relocateMenuDrawer() {
    var drawer = document.getElementById('MobileMenuDrawer');
    if (!drawer || drawer.parentElement === document.body) return;

    $$('body > [data-drawer="menu"]').forEach(function (stale) {
      if (stale !== drawer) stale.remove();
    });

    document.body.appendChild(drawer);
  }

  function bindDrawerTriggers() {
    document.addEventListener('click', function (event) {
      var menuToggle = event.target.closest('[data-menu-toggle]');
      if (menuToggle) {
        event.preventDefault();
        Drawers.open('menu');
        return;
      }

      var searchToggle = event.target.closest('[data-search-toggle]');
      if (searchToggle) {
        event.preventDefault();
        Drawers.open('search');
        return;
      }

      var cartToggle = event.target.closest('[data-cart-toggle]');
      if (cartToggle && $('[data-cart-drawer]') && settings.cartDrawer) {
        event.preventDefault();
        Drawers.open('cart');
        return;
      }

      var closer = event.target.closest('[data-drawer-close]');
      if (closer) {
        event.preventDefault();
        Drawers.close(closer.closest('[data-drawer]'));
        return;
      }

      if (event.target.closest('[data-overlay]')) {
        Drawers.closeAll();
        return;
      }

      // Close any open header dropdown when the click lands outside it.
      $$('[data-submenu][open]').forEach(function (details) {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;

      var openDrawer = $('[data-drawer].is-open');
      if (openDrawer) {
        Drawers.close(openDrawer);
        return;
      }

      var openZoom = $('[data-zoom-modal].is-open');
      if (openZoom) closeZoom(openZoom);
    });
  }

  /* ------------------------------------------------------------- Cart ---- */

  var Cart = {
    request: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var error = new Error(data.description || data.message || strings.cartError);
            error.data = data;
            throw error;
          }
          return data;
        });
      });
    },

    add: function (items, discountCode) {
      var payload = {
        items: items,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      };

      var chain = discountCode
        ? fetch('/discount/' + encodeURIComponent(discountCode), { credentials: 'same-origin' }).catch(function () {
            /* A missing or expired code must never block adding to cart. */
          })
        : Promise.resolve();

      return chain.then(function () {
        return Cart.request(routes.cartAdd, payload);
      });
    },

    change: function (line, quantity) {
      return this.request(routes.cartChange, {
        line: line,
        quantity: quantity,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      });
    },

    renderSections: function (data) {
      var sections = data && data.sections;
      if (!sections || !sections['cart-drawer']) return null;

      var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var freshDrawer = parsed.querySelector('[data-cart-drawer]');
      var currentDrawer = $('[data-cart-drawer]');

      if (!freshDrawer || !currentDrawer) return null;

      var freshPanel = freshDrawer.querySelector('.drawer__panel');
      var currentPanel = currentDrawer.querySelector('.drawer__panel');

      if (freshPanel && currentPanel) {
        currentPanel.replaceWith(freshPanel);

        // The panel that comes back from the Section Rendering API is brand new
        // DOM, so anything bound by JavaScript — the cross-sell carousel's
        // arrows — lost its listeners with the nodes they were attached to.
        // Re-bind inside the drawer, the same way shopify:section:load does
        // after the theme editor re-renders a section.
        initCarousels(currentDrawer);
        initPagedCarousels(currentDrawer);
      }

      var count = parseInt(freshDrawer.getAttribute('data-cart-item-count'), 10) || 0;
      currentDrawer.setAttribute('data-cart-item-count', String(count));
      this.updateCount(count);

      return count;
    },

    updateCount: function (count) {
      $$('[data-cart-count]').forEach(function (node) {
        node.textContent = count;
        node.classList.toggle('is-empty', count === 0);
      });
    }
  };

  function toast(message) {
    var el = $('[data-cart-toast]');
    if (!el) return;

    var text = $('[data-cart-toast-text]', el);
    if (text) text.textContent = message;

    el.hidden = false;
    void el.offsetWidth;
    el.classList.add('is-visible');

    clearTimeout(el.evoraToastTimer);
    el.evoraToastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () {
        el.hidden = true;
      }, 280);
    }, 2600);
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.disabled = loading;
  }

  /* ------------------------------------------------- Quick add & forms --- */

  function bindQuickAdd() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-quick-add-form]');
      if (!form) return;

      event.preventDefault();

      var button = $('[data-quick-add-button]', form);
      var label = $('[data-quick-add-label]', form);
      var optionsWrap = $('[data-quick-add-options]', form);

      // Remember the button's own text ("Quick Add" or "Add to cart", per
      // Theme settings > Product cards) so it can go back to it afterwards.
      if (label && !label.dataset.defaultLabel) label.dataset.defaultLabel = label.textContent;

      // Multi-variant products reveal an inline picker before adding, so the
      // customer never leaves the grid.
      if (optionsWrap && optionsWrap.hidden) {
        optionsWrap.hidden = false;
        if (label) label.textContent = strings.addToCart;
        var select = $('[data-quick-add-select]', form);
        if (select) select.focus();
        return;
      }

      var idField = $('[data-quick-add-select]', form);
      var variantId = idField && idField.value;
      if (!variantId) return;

      setButtonLoading(button, true);

      Cart.add([{ id: Number(variantId), quantity: 1 }])
        .then(function (data) {
          Cart.renderSections(data);
          if (label) label.textContent = strings.added;
          toast(strings.added);
          if (settings.cartDrawer) Drawers.open('cart');

          setTimeout(function () {
            if (label) label.textContent = optionsWrap ? strings.addToCart : label.dataset.defaultLabel || strings.quickAdd;
          }, 2200);
        })
        .catch(function (error) {
          toast(error.message || strings.cartError);
        })
        .finally(function () {
          setButtonLoading(button, false);
        });
    });
  }

  /* ------------------------------------------- Cart drawer interactions -- */

  function bindCartInteractions() {
    document.addEventListener('click', function (event) {
      var stepper = event.target.closest('[data-quantity-change]');
      if (stepper) {
        var wrap = stepper.closest('[data-quantity]');
        var input = $('[data-quantity-input]', wrap);
        if (!input) return;

        var min = parseInt(input.getAttribute('min'), 10);
        if (isNaN(min)) min = 0;

        var next = (parseInt(input.value, 10) || 0) + parseInt(stepper.dataset.quantityChange, 10);
        if (next < min) next = min;

        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      var remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        updateLine(remove.dataset.line, 0, remove);
      }
    });

    document.addEventListener('change', function (event) {
      var input = event.target.closest('[data-quantity-input]');
      if (!input || !input.dataset.line) return;

      updateLine(input.dataset.line, parseInt(input.value, 10) || 0, input);
    });

    // Order notes are saved without reloading.
    document.addEventListener(
      'change',
      function (event) {
        var note = event.target.closest('[data-cart-note]');
        if (!note) return;

        fetch(routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value })
        });
      },
      true
    );
  }

  function updateLine(line, quantity, origin) {
    var item = origin && origin.closest('[data-cart-item]');
    if (item) item.style.opacity = '0.5';

    Cart.change(parseInt(line, 10), quantity)
      .then(function (data) {
        var rendered = Cart.renderSections(data);

        // The cart page is server rendered, so reload it to stay in sync.
        if (rendered === null || $('[data-cart-page]')) {
          window.location.reload();
        }
      })
      .catch(function (error) {
        if (item) item.style.opacity = '';
        toast(error.message || strings.cartError);
      });
  }

  /* -------------------------------------------------- Predictive search -- */

  function bindPredictiveSearch() {
    var input = $('[data-predictive-search-input]');
    var results = $('[data-predictive-search-results]');

    if (!input || !results || !settings.predictiveSearch) return;

    var controller = null;

    var run = debounce(function () {
      var term = input.value.trim();

      if (term.length < 2) {
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();

      var url =
        routes.predictiveSearch +
        '?q=' +
        encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=' +
        (settings.searchLimit || 6) +
        '&section_id=predictive-search';

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var section = parsed.querySelector('#shopify-section-predictive-search') || parsed.body;
          results.innerHTML = section.innerHTML;
          input.setAttribute('aria-expanded', 'true');
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') results.innerHTML = '';
        });
    }, 250);

    input.addEventListener('input', run);
  }

  /* ------------------------------------------------------ Hero slider ---- */

  function initSliders(root) {
    $$('[data-slider]', root).forEach(function (slider) {
      var track = $('[data-slider-track]', slider);
      var slides = $$('[data-slider-slide]', slider);
      var dots = $$('[data-slider-dot]', slider);
      var prev = $('[data-slider-prev]', slider);
      var next = $('[data-slider-next]', slider);

      if (!track || slides.length < 1) return;

      var index = 0;
      var timer = null;
      var autoplay = slider.dataset.autoplay === 'true';
      var speed = parseInt(slider.dataset.autoplaySpeed, 10) || 3000;
      var sign = isRTL ? 1 : -1;

      function render() {
        track.style.transform = 'translateX(' + sign * index * 100 + '%)';

        slides.forEach(function (slide, i) {
          var active = i === index;
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          $$('a, button', slide).forEach(function (el) {
            if (active) {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', '-1');
            }
          });
        });

        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
      }

      function goTo(target) {
        index = (target + slides.length) % slides.length;
        render();
      }

      function start() {
        if (!autoplay || slides.length < 2) return;
        stop();
        timer = setInterval(function () {
          goTo(index + 1);
        }, speed);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      // Manual interaction pauses autoplay, then it picks up again.
      function interact(fn) {
        stop();
        fn();
        setTimeout(start, speed * 2);
      }

      if (prev) {
        prev.addEventListener('click', function () {
          interact(function () {
            goTo(index - 1);
          });
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          interact(function () {
            goTo(index + 1);
          });
        });
      }

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          interact(function () {
            goTo(parseInt(dot.dataset.index, 10) || 0);
          });
        });
      });

      var startX = null;
      slider.addEventListener(
        'touchstart',
        function (event) {
          startX = event.touches[0].clientX;
          stop();
        },
        { passive: true }
      );

      slider.addEventListener(
        'touchend',
        function (event) {
          if (startX === null) return;
          var delta = event.changedTouches[0].clientX - startX;
          if (Math.abs(delta) > 45) {
            var forward = isRTL ? delta > 0 : delta < 0;
            goTo(forward ? index + 1 : index - 1);
          }
          startX = null;
          setTimeout(start, speed * 2);
        },
        { passive: true }
      );

      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          stop();
        } else {
          start();
        }
      });

      render();
      start();
    });
  }

  /* ------------------------------------------------- Before and after ---- */

  function initCompare(root) {
    $$('[data-compare]', root).forEach(function (compare) {
      var range = $('[data-compare-range]', compare);
      if (!range) return;

      function apply() {
        compare.style.setProperty('--compare-position', range.value + '%');
      }

      range.addEventListener('input', apply);
      range.addEventListener('change', apply);
      apply();
    });
  }

  /* ---------------------------------------------------------- Carousels -- */

  function initCarousels(root) {
    $$('[data-carousel]', root).forEach(function (carousel) {
      var track = $('[data-carousel-track]', carousel);
      var prev = $('[data-carousel-prev]', carousel);
      var next = $('[data-carousel-next]', carousel);

      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        return first ? first.getBoundingClientRect().width + 12 : 220;
      }

      function scrollBy(direction) {
        track.scrollBy({ left: direction * step() * (isRTL ? -1 : 1), behavior: 'smooth' });
      }

      function syncArrows() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        var position = Math.abs(track.scrollLeft);
        prev.disabled = position <= 2;
        next.disabled = position >= max;
      }

      if (prev) {
        prev.addEventListener('click', function () {
          scrollBy(-1);
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          scrollBy(1);
        });
      }

      track.addEventListener('scroll', debounce(syncArrows, 90));
      syncArrows();
    });
  }

  /* ----------------------------------------------------- Paged carousel -- */

  // Drives snippets/carousel.liquid. The row advances one whole page at a time
  // from the arrows or the dots and the viewport itself never scrolls, so there
  // is no free drag or swipe. How many items make up a page comes from the
  // --carousel-per-view custom property, which the stylesheet switches per
  // breakpoint — a resize is therefore just a re-read of the computed value.
  function initPagedCarousels(root) {
    $$('[data-carousel-paged]', root).forEach(function (carousel) {
      if (carousel.dataset.carouselReady === 'true') return;

      var viewport = $('[data-carousel-viewport]', carousel);
      var track = $('[data-carousel-track]', carousel);
      var prev = $('[data-carousel-prev]', carousel);
      var next = $('[data-carousel-next]', carousel);
      var dots = $$('[data-carousel-dot]', carousel);

      if (!viewport || !track) return;

      var items = $$('.carousel__item', track);
      if (!items.length) return;

      var index = 0;
      var perView = 1;
      var pages = 1;

      // Page geometry is derived from the same arithmetic the stylesheet uses,
      // rather than measured, so it stays exact while the row is mid-transition.
      function metrics() {
        var pageWidth = viewport.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap);
        if (isNaN(gap)) gap = 0;
        var itemWidth = (pageWidth - (perView - 1) * gap) / perView;
        var contentWidth = items.length * itemWidth + (items.length - 1) * gap;

        return {
          pageWidth: pageWidth,
          gap: gap,
          itemWidth: itemWidth,
          maxOffset: Math.max(0, contentWidth - pageWidth)
        };
      }

      // Off-page items leave the tab order, so a keyboard user never lands on a
      // card that is not on screen. The original tabindex is stashed first,
      // because the product card already parks one on its image link.
      function setReachable(item, reachable) {
        item.setAttribute('aria-hidden', reachable ? 'false' : 'true');

        $$('a, button, input, select, textarea, [tabindex]', item).forEach(function (el) {
          if (reachable) {
            if (!el.hasAttribute('data-carousel-tabindex')) return;
            var original = el.getAttribute('data-carousel-tabindex');
            if (original === '') {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', original);
            }
            el.removeAttribute('data-carousel-tabindex');
          } else if (!el.hasAttribute('data-carousel-tabindex')) {
            el.setAttribute('data-carousel-tabindex', el.getAttribute('tabindex') || '');
            el.setAttribute('tabindex', '-1');
          }
        });
      }

      function render() {
        var box = metrics();
        var offset = Math.min(index * (box.pageWidth + box.gap), box.maxOffset);

        carousel.style.setProperty('--carousel-offset', offset + 'px');

        items.forEach(function (item, i) {
          var start = i * (box.itemWidth + box.gap);
          setReachable(item, start >= offset - 1 && start + box.itemWidth <= offset + box.pageWidth + 1);
        });

        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= pages - 1;

        dots.forEach(function (dot, i) {
          var active = i === index;
          dot.hidden = i >= pages;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        carousel.classList.toggle('carousel--static', pages <= 1);
      }

      function goTo(target) {
        index = Math.max(0, Math.min(target, pages - 1));
        render();
      }

      function layout() {
        var value = parseFloat(getComputedStyle(carousel).getPropertyValue('--carousel-per-view'));
        perView = value > 0 ? Math.round(value) : 1;
        pages = Math.max(1, Math.ceil(items.length / perView));
        if (index > pages - 1) index = pages - 1;
        render();
        syncAutoplay();
      }

      /* Autoplay. Advances one page at a time and loops from the last page
         back to the first; the manual arrows still stop at the ends. It only
         runs while every condition holds:
           - switched on in Theme settings > Carousels
           - the visitor has not asked for reduced motion
           - there is more than one page
           - at least a third of the carousel is on screen and the tab is visible
           - the pointer is not over it and focus is not inside it (both only
             pause — it carries on when they leave)
           - nobody has used it yet: an arrow, a dot, an arrow key or any touch
             or click inside it stops autoplay on that carousel for the rest of
             the page view. Starting again after a delay would pull content
             away from someone who has just shown they are reading it. */
      var autoplay = {
        enabled: settings.carouselAutoplay !== false,
        delay: Math.max(2, parseFloat(settings.carouselAutoplaySpeed) || 5) * 1000,
        stopped: false,
        hovering: false,
        focused: false,
        visible: typeof IntersectionObserver !== 'function',
        timer: null
      };
      var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

      function canAutoplay() {
        return (
          autoplay.enabled &&
          !autoplay.stopped &&
          autoplay.visible &&
          !autoplay.hovering &&
          !autoplay.focused &&
          !document.hidden &&
          pages > 1 &&
          !(reducedMotion && reducedMotion.matches) &&
          carousel.isConnected
        );
      }

      // A pause restarts the full interval rather than resuming mid-count, so
      // the row never jumps the instant the pointer leaves.
      function syncAutoplay() {
        var run = canAutoplay();
        if (run && !autoplay.timer) {
          autoplay.timer = setTimeout(advance, autoplay.delay);
        } else if (!run && autoplay.timer) {
          clearTimeout(autoplay.timer);
          autoplay.timer = null;
        }
      }

      function advance() {
        autoplay.timer = null;
        if (!canAutoplay()) return;
        goTo(index >= pages - 1 ? 0 : index + 1);
        syncAutoplay();
      }

      function stopAutoplay() {
        autoplay.stopped = true;
        syncAutoplay();
      }

      if (autoplay.enabled) {
        if (!autoplay.visible) {
          new IntersectionObserver(
            function (entries) {
              autoplay.visible = entries[0].isIntersecting;
              syncAutoplay();
            },
            { threshold: 0.35 }
          ).observe(carousel);
        }

        carousel.addEventListener('mouseenter', function () {
          autoplay.hovering = true;
          syncAutoplay();
        });

        carousel.addEventListener('mouseleave', function () {
          autoplay.hovering = false;
          syncAutoplay();
        });

        carousel.addEventListener('focusin', function () {
          autoplay.focused = true;
          syncAutoplay();
        });

        carousel.addEventListener('focusout', function (event) {
          if (event.relatedTarget && carousel.contains(event.relatedTarget)) return;
          autoplay.focused = false;
          syncAutoplay();
        });

        // Any touch or click inside — a swipe attempt, a card, Quick add, the
        // before/after handle — counts as taking over.
        carousel.addEventListener('pointerdown', stopAutoplay);

        document.addEventListener('visibilitychange', syncAutoplay);

        if (reducedMotion) {
          if (reducedMotion.addEventListener) {
            reducedMotion.addEventListener('change', syncAutoplay);
          } else if (reducedMotion.addListener) {
            reducedMotion.addListener(syncAutoplay);
          }
        }
      }

      if (prev) {
        prev.addEventListener('click', function () {
          stopAutoplay();
          goTo(index - 1);
        });
      }

      if (next) {
        next.addEventListener('click', function () {
          stopAutoplay();
          goTo(index + 1);
        });
      }

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          stopAutoplay();
          goTo(parseInt(dot.dataset.index, 10) || 0);
        });
      });

      // Arrow keys page the row once focus is inside the control cluster.
      carousel.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (!event.target.closest('[data-carousel-controls]')) return;

        event.preventDefault();
        stopAutoplay();
        var forward = isRTL ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
        goTo(forward ? index + 1 : index - 1);
      });

      // The viewport is not a scroller. If the browser nudges it anyway — an
      // element inside gaining focus, say — put it straight back.
      viewport.addEventListener('scroll', function () {
        if (viewport.scrollLeft !== 0) viewport.scrollLeft = 0;
      });

      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(debounce(layout, 100)).observe(viewport);
      } else {
        window.addEventListener('resize', debounce(layout, 150));
      }

      // Lets the customizer jump to the page holding the block being edited,
      // and keeps autoplay from carrying it away again while it is edited.
      carousel.evoraCarousel = {
        goToItem: function (item) {
          var position = items.indexOf(item);
          if (position < 0) return;
          stopAutoplay();
          goTo(Math.floor(position / perView));
        }
      };

      carousel.dataset.carouselReady = 'true';
      carousel.classList.add('is-ready');
      layout();
    });
  }

  /* ------------------------------------------------------ Small pieces --- */

  function bindLanguageSelectors() {
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-language-toggle]');

      $$('[data-language-list]').forEach(function (list) {
        var wrapper = list.closest('[data-language-selector]');
        var owner = wrapper && $('[data-language-toggle]', wrapper);
        var shouldOpen = toggle && owner === toggle && list.hidden;

        list.hidden = !shouldOpen;
        if (owner) owner.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      });
    });

    // The mobile drawer uses a native select, which submits on change.
    document.addEventListener('change', function (event) {
      var select = event.target.closest('[data-locale-select]');
      if (select && select.form) select.form.submit();
    });
  }

  function bindSortForm(root) {
    var select = $('[data-sort-select]', root);
    if (!select) return;

    select.addEventListener('change', function () {
      if (select.form) select.form.submit();
    });
  }

  function bindReviews(root) {
    var more = $('[data-reviews-more]', root);
    if (more) {
      more.addEventListener('click', function () {
        $$('[data-review][hidden]').forEach(function (review) {
          review.hidden = false;
          review.classList.remove('is-hidden');
        });
        more.hidden = true;
      });
    }

    var toggle = $('[data-review-form-toggle]', root);
    var form = $('[data-review-form]', root);

    if (toggle && form) {
      toggle.addEventListener('click', function () {
        var opening = form.hidden;
        form.hidden = !opening;
        toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
        if (opening) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      // Keep the form open when Shopify re-renders the page after a submit.
      if (form.querySelector('.review-form__success, .review-form__error')) {
        form.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      }
    }

    clearReviewSubmission();
  }

  // After a contact-form submit Shopify redirects back to the product with
  // ?contact_posted=true, and form.posted_successfully? is read from that
  // parameter — not from the POST itself. Left in the address bar, every
  // refresh would show "Thank you" again. So once the message has been shown,
  // drop the parameter (and the #ReviewContactForm jump) from the URL without
  // reloading: a refresh then loads the plain product page and an empty form.
  function clearReviewSubmission() {
    if (!window.history.replaceState || !$('#ReviewContactForm')) return;

    var url = new URL(window.location.href);
    if (!url.searchParams.has('contact_posted')) return;

    url.searchParams.delete('contact_posted');
    if (url.hash === '#ReviewContactForm') url.hash = '';
    window.history.replaceState(window.history.state, '', url.toString());
  }

  // Coming back to the product with the Back button can restore the page
  // exactly as it was left, "Thank you" and all. Clear the old status so the
  // form reads as fresh.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    $$('#ReviewContactForm .review-form__success, #ReviewContactForm .review-form__error').forEach(function (message) {
      message.remove();
    });
  });

  function bindRecoverPasswordToggle() {
    var login = $('#CustomerLogin');
    var recover = $('#RecoverPassword');
    if (!login || !recover) return;

    $$('[data-toggle-recover]').forEach(function (button) {
      button.addEventListener('click', function () {
        var showRecover = recover.hidden;
        recover.hidden = !showRecover;
        login.hidden = showRecover;
      });
    });

    if (window.location.hash === '#recover') {
      recover.hidden = false;
      login.hidden = true;
    }
  }

  function bindAnchorScroll() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('[data-scroll-to]');
      if (!link) return;

      var target = document.querySelector(link.getAttribute('href'));
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function closeZoom(modal) {
    modal.classList.remove('is-open');
    setTimeout(function () {
      modal.hidden = true;
    }, 220);
    document.body.classList.remove('drawer-open');
  }

  window.EvoraTheme = {
    Drawers: Drawers,
    Cart: Cart,
    toast: toast,
    formatMoney: formatMoney,
    setButtonLoading: setButtonLoading,
    closeZoom: closeZoom,
    helpers: { $: $, $$: $$, debounce: debounce, isRTL: isRTL }
  };

  function init() {
    relocateMenuDrawer();
    bindDrawerTriggers();
    bindQuickAdd();
    bindCartInteractions();
    bindPredictiveSearch();
    initSliders();
    initCompare();
    initCarousels();
    initPagedCarousels();
    bindLanguageSelectors();
    bindSortForm();
    bindReviews();
    bindRecoverPasswordToggle();
    bindAnchorScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support: re-run the behaviour of a section after it is
  // re-rendered, so the customizer preview stays interactive.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target;
    relocateMenuDrawer();
    initSliders(root);
    initCompare(root);
    initCarousels(root);
    initPagedCarousels(root);
    bindSortForm(root);
    bindReviews(root);
  });

  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.id === 'shopify-section-cart-drawer') Drawers.open('cart');
  });

  // Selecting a block in the customizer pages the carousel to it, so the
  // merchant always sees the card they are editing.
  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target.closest('.carousel__item');
    if (!item) return;

    var carousel = item.closest('[data-carousel-paged]');
    if (carousel && carousel.evoraCarousel) carousel.evoraCarousel.goToItem(item);
  });
})();
